import { ReadOutlined, ReloadOutlined } from '@ant-design/icons'
import {
	Alert,
	Button,
	Card,
	Input,
	Select,
	Space,
	Switch,
	Table,
	Tag,
	Tooltip,
	message,
} from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ListPageIntro, ListPageShell } from '../../common'
import {
	getCategoryDisplayName,
	getParentChipPreview,
	listCourseCategoryLeaves,
	resetCourseCategoryNewSeed,
	setCourseCategoryIsNew,
} from '../../services/courseCategoryNew.service'
import './CategoryNewList.scss'

const NEW_FILTER = {
	ALL: 'all',
	NEW: 'new',
	NOT_NEW: 'not_new',
}

const isMockMode =
	String(process.env.REACT_APP_USE_CATEGORY_NEW_MOCK || '').toLowerCase() ===
	'true'

export const CategoryNewList = () => {
	const [items, setItems] = useState([])
	const [parents, setParents] = useState([])
	const [total, setTotal] = useState(0)
	const [newCount, setNewCount] = useState(0)
	const [loading, setLoading] = useState(false)
	const [page, setPage] = useState(0)
	const [pageSize, setPageSize] = useState(50)
	const [query, setQuery] = useState('')
	const [searchKey, setSearchKey] = useState(0)
	const [parentId, setParentId] = useState(undefined)
	const [newFilter, setNewFilter] = useState(NEW_FILTER.ALL)
	const [previewParentId, setPreviewParentId] = useState(null)
	const [preview, setPreview] = useState(null)
	const [previewLoading, setPreviewLoading] = useState(false)
	const [pendingIds, setPendingIds] = useState(() => new Set())
	/** Bumps when New flags change so chip preview reloads from store. */
	const [previewEpoch, setPreviewEpoch] = useState(0)
	const mountedRef = useRef(true)
	const requestSeq = useRef(0)

	useEffect(() => {
		mountedRef.current = true
		return () => {
			mountedRef.current = false
		}
	}, [])

	const isNewParam = useMemo(() => {
		if (newFilter === NEW_FILTER.NEW) return true
		if (newFilter === NEW_FILTER.NOT_NEW) return false
		return undefined
	}, [newFilter])

	const load = useCallback(async () => {
		const seq = ++requestSeq.current
		setLoading(true)
		try {
			const rs = await listCourseCategoryLeaves({
				page,
				pageSize,
				q: query.trim() || undefined,
				parentId,
				isNew: isNewParam,
			})
			if (!mountedRef.current || seq !== requestSeq.current) return
			setItems(Array.isArray(rs?.data) ? rs.data : [])
			setTotal(Number(rs?.total) || 0)
			setNewCount(Number(rs?.newCount) || 0)
			setParents(Array.isArray(rs?.parents) ? rs.parents : [])
			setPreviewParentId(prev => {
				if (prev != null) return prev
				const firstWithSubs = (rs?.parents || []).find(p => p.subCount > 0)
				return firstWithSubs?.id ?? null
			})
		} catch (err) {
			if (!mountedRef.current || seq !== requestSeq.current) return
			message.error(err?.message || 'Could not load categories.')
		} finally {
			if (mountedRef.current && seq === requestSeq.current) {
				setLoading(false)
			}
		}
	}, [page, pageSize, query, parentId, isNewParam])

	useEffect(() => {
		load()
	}, [load])

	useEffect(() => {
		if (previewParentId == null) {
			setPreview(null)
			return undefined
		}
		let cancelled = false
		setPreviewLoading(true)
		getParentChipPreview(previewParentId)
			.then(rs => {
				if (!cancelled) setPreview(rs)
			})
			.catch(err => {
				if (!cancelled) {
					setPreview(null)
					message.error(err?.message || 'Could not load chip preview.')
				}
			})
			.finally(() => {
				if (!cancelled) setPreviewLoading(false)
			})
		return () => {
			cancelled = true
		}
	}, [previewParentId, previewEpoch])

	const setPending = (id, on) => {
		setPendingIds(prev => {
			const next = new Set(prev)
			if (on) next.add(Number(id))
			else next.delete(Number(id))
			return next
		})
	}

	const handleToggle = async (record, checked) => {
		const id = Number(record.id)
		if (pendingIds.has(id)) return

		const previous = Boolean(record.isNew)
		setPending(id, true)
		// Optimistic UI
		setItems(prev =>
			prev.map(row =>
				Number(row.id) === id
					? { ...row, isNew: checked, updatedAt: new Date().toISOString() }
					: row
			)
		)
		setNewCount(prev => {
			if (previous === checked) return prev
			return checked ? prev + 1 : Math.max(0, prev - 1)
		})

		try {
			await setCourseCategoryIsNew(id, checked)
			if (!mountedRef.current) return
			message.success(
				checked
					? 'Marked as New — will pin to top on web & mobile.'
					: 'Removed New badge.'
			)
			setPreviewEpoch(v => v + 1)
			// Refresh list sort from store (source of truth)
			await load()
		} catch (err) {
			if (!mountedRef.current) return
			setItems(prev =>
				prev.map(row =>
					Number(row.id) === id ? { ...row, isNew: previous } : row
				)
			)
			setNewCount(prev => {
				if (previous === checked) return prev
				return previous ? prev + 1 : Math.max(0, prev - 1)
			})
			message.error(err?.message || 'Could not update New flag.')
		} finally {
			if (mountedRef.current) setPending(id, false)
		}
	}

	const handleResetSeed = async () => {
		try {
			await resetCourseCategoryNewSeed()
			message.success('Mock data reset to production-like seed.')
			setPage(0)
			setQuery('')
			setSearchKey(k => k + 1)
			setParentId(undefined)
			setNewFilter(NEW_FILTER.ALL)
			setPreviewParentId(null)
			setPreviewEpoch(v => v + 1)
			await load()
		} catch (err) {
			message.error(err?.message || 'Could not reset mock data.')
		}
	}

	const parentOptions = useMemo(
		() =>
			parents.map(p => ({
				value: p.id,
				label: `${p.nameEn} (${p.newCount}/${p.subCount} New)`,
			})),
		[parents]
	)

	const columns = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
			width: 72,
		},
		{
			title: 'Parent',
			dataIndex: 'parentNameEn',
			key: 'parent',
			width: 180,
			ellipsis: true,
			render: (_, record) => (
				<span>
					{record.parentNameEn}
					{record.parentNameVi &&
						record.parentNameVi !== record.parentNameEn && (
							<span className="category-new-list__muted">
								{' '}
								· {record.parentNameVi}
							</span>
						)}
				</span>
			),
		},
		{
			title: 'Name (EN)',
			key: 'nameEn',
			ellipsis: true,
			render: (_, record) => getCategoryDisplayName(record, 'en'),
		},
		{
			title: 'Name (VI)',
			key: 'nameVi',
			ellipsis: true,
			render: (_, record) => getCategoryDisplayName(record, 'vi'),
		},
		{
			title: 'Badge',
			key: 'badge',
			width: 100,
			render: (_, record) =>
				record.isNew ? (
					<span className="category-new-list__badge" aria-label="New">
						New
					</span>
				) : (
					<span className="category-new-list__muted">—</span>
				),
		},
		{
			title: 'New',
			key: 'isNew',
			width: 100,
			render: (_, record) => (
				<Tooltip
					title={
						record.isNew
							? 'On: badge + pin to top of chips'
							: 'Off: normal chip order'
					}
				>
					<Switch
						checked={Boolean(record.isNew)}
						loading={pendingIds.has(Number(record.id))}
						disabled={pendingIds.has(Number(record.id))}
						onChange={checked => handleToggle(record, checked)}
						checkedChildren="ON"
						unCheckedChildren="OFF"
					/>
				</Tooltip>
			),
		},
		{
			title: 'Updated',
			dataIndex: 'updatedAt',
			key: 'updatedAt',
			width: 170,
			render: value => (value ? new Date(value).toLocaleString() : '—'),
		},
		{
			title: 'Preview parent',
			key: 'preview',
			width: 130,
			render: (_, record) => (
				<Button
					size="small"
					type={
						Number(previewParentId) === Number(record.parentId)
							? 'primary'
							: 'default'
					}
					onClick={() => setPreviewParentId(record.parentId)}
				>
					Show chips
				</Button>
			),
		},
	]

	return (
		<ListPageShell className="category-new-list">
			<ListPageIntro
				icon={ReadOutlined}
				title="Course category — New badge"
				subtitle={
					isMockMode
						? 'Mark subcategory chips as New (badge + pin to top). Mock mode — set REACT_APP_USE_CATEGORY_NEW_MOCK=false to use live API.'
						: 'Mark subcategory chips as New (badge + pin to top). Live editor API — changes apply to web & mobile after clients read isNew.'
				}
			/>

			<Alert
				className="category-new-list__alert"
				type="info"
				showIcon
				message="How clients will use this"
				description="Web & mobile read isNew from category API. Chips with isNew show a New badge and are sorted above other chips in the same parent. No limit on how many can be New."
			/>

			<div className="category-new-list__stats">
				<Tag color="green">New active: {newCount}</Tag>
				<Tag>Leaves listed: {total}</Tag>
			</div>

			<div className="category-new-list__toolbar">
				<Space wrap>
					<Input.Search
						key={searchKey}
						allowClear
						placeholder="Search id, parent, name EN/VI"
						onSearch={value => {
							setPage(0)
							setQuery(value || '')
						}}
						style={{ width: 280 }}
					/>
					<Select
						allowClear
						placeholder="Parent category"
						style={{ width: 280 }}
						value={parentId}
						options={parentOptions}
						onChange={value => {
							setPage(0)
							setParentId(value)
							if (value != null) setPreviewParentId(value)
						}}
					/>
					<Select
						style={{ width: 150 }}
						value={newFilter}
						onChange={value => {
							setPage(0)
							setNewFilter(value)
						}}
						options={[
							{ value: NEW_FILTER.ALL, label: 'All' },
							{ value: NEW_FILTER.NEW, label: 'New only' },
							{ value: NEW_FILTER.NOT_NEW, label: 'Not New' },
						]}
					/>
					{isMockMode && (
						<Tooltip title="Restore Wellbeing #32 & Climate Change #34 as New">
							<Button icon={<ReloadOutlined />} onClick={handleResetSeed}>
								Reset mock seed
							</Button>
						</Tooltip>
					)}
				</Space>
			</div>

			<div className="category-new-list__layout">
				<div className="category-new-list__table-wrap">
					<Table
						rowKey="id"
						loading={loading}
						columns={columns}
						dataSource={items}
						scroll={{ x: 1100 }}
						rowClassName={record =>
							record.isNew ? 'category-new-list__row--new' : ''
						}
						pagination={{
							current: page + 1,
							pageSize,
							total,
							showSizeChanger: true,
							pageSizeOptions: ['20', '50', '100'],
							showTotal: t => `${t} categor${t === 1 ? 'y' : 'ies'}`,
							onChange: (nextPage, nextSize) => {
								setPage(nextPage - 1)
								setPageSize(nextSize)
							},
						}}
					/>
				</div>

				<Card
					className="category-new-list__preview"
					size="small"
					loading={previewLoading}
					title={
						preview
							? `Chip order — ${preview.parent.nameEn}`
							: 'Chip order preview'
					}
					extra={
						<Select
							size="small"
							style={{ width: 200 }}
							placeholder="Parent"
							value={previewParentId ?? undefined}
							options={parentOptions}
							onChange={setPreviewParentId}
						/>
					}
				>
					{!preview || !preview.chips?.length ? (
						<p className="category-new-list__muted">
							Select a parent to preview chip order (New pinned first).
						</p>
					) : (
						<>
							<p className="category-new-list__preview-hint">
								Same order web/mobile will render under this parent.
							</p>
							<div className="category-new-list__chips">
								{preview.chips.map(chip => (
									<div
										key={chip.id}
										className={`category-new-list__chip${
											chip.isNew ? ' is-new' : ''
										}`}
									>
										<span className="category-new-list__chip-name">
											{chip.nameEn}
										</span>
										{chip.isNew && (
											<span
												className="category-new-list__badge"
												aria-label="New"
											>
												New
											</span>
										)}
									</div>
								))}
							</div>
						</>
					)}
				</Card>
			</div>
		</ListPageShell>
	)
}
